/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import { useState, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import MDInput from "components/MDInput";
import axios from "axios";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_HOHOLOK } from "utils";

// Data

// Fungsi format rupiah
function formatRupiah(angka) {
  if (!angka && angka !== 0) return "";

  // Konversi ke number untuk memastikan tipe data yang benar
  const numAngka = Number(angka);
  if (isNaN(numAngka)) return "";

  // Format angka dengan pemisah ribuan dan 2 desimal
  const formatted = numAngka.toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return "Rp " + formatted;
}

function Detail_Proyek() {
  const navigate = useNavigate();
  const [proyekList, setProyekList] = useState([]);
  const [selectedProyek, setSelectedProyek] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openKurvaS, setOpenKurvaS] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [kurvaSForm, setKurvaSForm] = useState([
    { minggu_ke: "", progres_rencana: "" },
  ]);
  const [kurvaSLoading, setKurvaSLoading] = useState(false);
  const [selectedUtama, setSelectedUtama] = useState(null);
  const [tahun, setTahun] = useState(new Date().getFullYear());

  // List satuan umum konstruksi
  const satuanOptions = [
    "m",
    "m2",
    "m3",
    "unit",
    "buah",
    "set",
    "kg",
    "liter",
    "pasang",
    "batang",
    "titik",
    "paket",
    "hari",
    "bulan",
    "orang",
    "lbr",
    "roll",
    "box",
    "zak",
    "cm",
    "mm",
    "km",
    "ton",
    "lusin",
    "rim",
    "ampere",
    "volt",
    "kwh",
    "kaleng",
    "karung",
    "bidang",
    "lembar",
    "papan",
    "buku",
    "keping",
    "ekor",
    "gr",
    "ml",
    "lbr",
    "ls",
  ];
  const [formUtama, setFormUtama] = useState({
    nama_pekerjaan: "",
  });
  const [subPekerjaan, setSubPekerjaan] = useState([
    { uraian_pekerjaan: "", volume: 0, satuan: "", harga_satuan: "" },
  ]);
  const [tableRows, setTableRows] = useState([]);
  const [tableColumns] = useState([
    { Header: "Uraian Pekerjaan", accessor: "uraian_pekerjaan", width: "35%" },
    { Header: "Volume", accessor: "volume", width: "8%" },
    { Header: "Satuan", accessor: "satuan", width: "8%" },
    { Header: "Harga Satuan", accessor: "harga_satuan", width: "12%" },
    { Header: "Total Harga", accessor: "total_harga", width: "12%" },
    { Header: "Bobot (%)", accessor: "bobot", width: "8%" },
    {
      Header: "Aksi",
      accessor: "aksi",
      width: "17%",
      Cell: ({ row }) => {
        if (row.original.isUtama) {
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <IconButton
                color="info"
                onClick={() => handleEdit(row.original)}
                size="small"
                disabled={loadingDelete}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => handleDelete(row.original.id_pekerjaan_utama)}
                size="small"
                disabled={loadingDelete}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          );
        }
        return null;
      },
    },
  ]);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_HOHOLOK}proyek/tahun/${tahun}`)
      .then((res) => {
        const data = res.data || [];
        const proyekListFormatted = data.map((item) => ({
          label: item.nama_proyek,
          value: item.id_proyek,
          ...item,
        }));
        setProyekList(proyekListFormatted);

        // Otomatis pilih proyek pertama jika ada data dan belum ada proyek yang dipilih
        if (proyekListFormatted.length > 0 && !selectedProyek) {
          setSelectedProyek(proyekListFormatted[0]);
        }

        setLoading(false);
      })
      .catch(() => {
        setProyekList([]);
        setLoading(false);
      });
  }, [tahun, selectedProyek]);

  // Fungsi untuk fetch pekerjaan utama & sub pekerjaan
  const fetchPekerjaanProyek = async (idProyek) => {
    try {
      const res = await axios.get(`${API_HOHOLOK}proyek/${idProyek}/pekerjaan`);
      const pekerjaanUtama = res.data?.pekerjaan_utama || [];

      // Hitung total harga semua sub pekerjaan untuk bobot global
      let allSubs = pekerjaanUtama.flatMap((utama) =>
        (utama.sub_pekerjaan || []).map((sub) => {
          // Pastikan semua nilai dikonversi ke number
          const hargaSatuan = Number(sub.harga_satuan) || 0;
          const volume = Number(sub.volume) || 0;
          const totalHarga = hargaSatuan * volume;
          return {
            id_pekerjaan_utama: utama.id_pekerjaan_utama,
            nama_pekerjaan: utama.nama_pekerjaan,
            uraian_pekerjaan: sub.uraian_pekerjaan,
            volume: volume,
            satuan: sub.satuan,
            harga_satuan: hargaSatuan,
            total_harga: totalHarga,
          };
        })
      );

      const totalAll = allSubs.reduce(
        (sum, s) => sum + (s.total_harga || 0),
        0
      );

      // Hitung total harga & bobot per pekerjaan utama
      const rows = [];
      pekerjaanUtama.forEach((utama) => {
        // Hitung total harga & bobot pekerjaan utama
        const subs = (utama.sub_pekerjaan || []).map((sub) => {
          // Pastikan semua nilai dikonversi ke number
          const hargaSatuan = Number(sub.harga_satuan) || 0;
          const volume = Number(sub.volume) || 0;
          const totalHarga = hargaSatuan * volume;

          // Hitung bobot sub pekerjaan terhadap totalAll
          const bobot =
            totalAll > 0 ? ((totalHarga / totalAll) * 100).toFixed(2) : "0.00";

          return {
            uraian_pekerjaan: (
              <span style={{ marginLeft: 24 }}>{sub.uraian_pekerjaan}</span>
            ),
            volume: volume,
            satuan: sub.satuan,
            harga_satuan: formatRupiah(hargaSatuan),
            total_harga: formatRupiah(totalHarga),
            bobot,
            isUtama: false,
          };
        });

        // Hitung total harga pekerjaan utama dari data allSubs yang sudah dihitung dengan benar
        const totalHargaUtamaFromAllSubs = allSubs
          .filter((sub) => sub.id_pekerjaan_utama === utama.id_pekerjaan_utama)
          .reduce((sum, sub) => sum + sub.total_harga, 0);

        const bobotUtama =
          totalAll > 0
            ? ((totalHargaUtamaFromAllSubs / totalAll) * 100).toFixed(2)
            : "0.00";

        // Baris judul pekerjaan utama (dengan total & bobot di kolom yang sama)
        rows.push({
          uraian_pekerjaan: <b>{utama.nama_pekerjaan}</b>,
          volume: "",
          satuan: "",
          harga_satuan: "",
          total_harga: <b>{formatRupiah(totalHargaUtamaFromAllSubs)}</b>,
          bobot: <b>{bobotUtama}</b>,
          isUtama: true,
          id_pekerjaan_utama: utama.id_pekerjaan_utama,
        });
        rows.push(...subs);
      });
      setTableRows(rows);
    } catch (error) {
      console.error("Error fetching pekerjaan proyek:", error);
      setTableRows([]);
    }
  };

  useEffect(() => {
    if (selectedProyek && selectedProyek.value) {
      fetchPekerjaanProyek(selectedProyek.value);
    } else {
      setTableRows([]);
    }
  }, [selectedProyek, refreshKey]);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEdit(false);
    setEditId(null);
    // Reset form
    setFormUtama({ nama_pekerjaan: "" });
    setSubPekerjaan([
      { uraian_pekerjaan: "", volume: 0, satuan: "", harga_satuan: "" },
    ]);
  };

  const handleUtamaChange = (e) => {
    const { name, value } = e.target;
    setFormUtama({ ...formUtama, [name]: value });
  };
  const handleSubChange = (idx, e) => {
    const { name, value } = e.target;
    const newSubs = [...subPekerjaan];
    if (name === "harga_satuan") {
      // Konversi ke number dan izinkan decimal
      const numValue = Number(value) || 0;
      newSubs[idx][name] = numValue;
    } else if (name === "volume") {
      // Pastikan volume juga dikonversi ke number
      const numValue = Number(value) || 0;
      newSubs[idx][name] = numValue;
    } else {
      newSubs[idx][name] = value;
    }
    setSubPekerjaan(newSubs);
  };
  const handleAddSub = () => {
    setSubPekerjaan([
      ...subPekerjaan,
      { uraian_pekerjaan: "", volume: 0, satuan: "", harga_satuan: "" },
    ]);
  };
  const handleRemoveSub = (idx) => {
    if (subPekerjaan.length === 1) return;
    setSubPekerjaan(subPekerjaan.filter((_, i) => i !== idx));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProyek) {
      swal("Pilih proyek terlebih dahulu", "", "warning");
      return;
    }
    // Validasi sederhana
    if (!formUtama.nama_pekerjaan) {
      swal("Nama pekerjaan utama wajib diisi", "", "warning");
      return;
    }
    if (
      subPekerjaan.some(
        (sub) =>
          !sub.uraian_pekerjaan ||
          !sub.volume ||
          !sub.satuan ||
          !sub.harga_satuan ||
          sub.volume <= 0 ||
          sub.harga_satuan <= 0
      )
    ) {
      swal(
        "Semua field sub pekerjaan wajib diisi dan nilai volume/harga harus lebih dari 0",
        "",
        "warning"
      );
      return;
    }
    // Siapkan payload
    const payload = {
      id_proyek: selectedProyek.value,
      nama_pekerjaan: formUtama.nama_pekerjaan,
      sub_pekerjaan: subPekerjaan.map((sub) => ({
        uraian_pekerjaan: sub.uraian_pekerjaan,
        volume: Number(sub.volume) || 0,
        satuan: sub.satuan,
        harga_satuan: Number(sub.harga_satuan) || 0,
      })),
    };
    try {
      if (isEdit && editId) {
        // Update existing pekerjaan utama
        await axios.put(`${API_HOHOLOK}pekerjaan-utama/${editId}`, {
          nama_pekerjaan: formUtama.nama_pekerjaan,
          sub_pekerjaan: subPekerjaan.map((sub) => ({
            uraian_pekerjaan: sub.uraian_pekerjaan,
            volume: Number(sub.volume) || 0,
            satuan: sub.satuan,
            harga_satuan: Number(sub.harga_satuan) || 0,
          })),
        });
        swal(
          "Berhasil",
          "Data pekerjaan utama & sub pekerjaan berhasil diperbarui",
          "success"
        );
      } else {
        // Create new pekerjaan utama
        await axios.post(`${API_HOHOLOK}pekerjaan-utama`, payload);
        swal(
          "Berhasil",
          "Data pekerjaan utama & sub pekerjaan berhasil disimpan",
          "success"
        );
      }
      handleCloseModal();
      // Fetch ulang data pekerjaan setelah submit
      fetchPekerjaanProyek(selectedProyek.value);
      // Increment refreshKey untuk memaksa re-render
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      swal(
        "Gagal",
        "Gagal menyimpan data pekerjaan utama & sub pekerjaan",
        "error"
      );
    }
  };

  const handleEdit = async (data) => {
    try {
      const response = await axios.get(
        `${API_HOHOLOK}pekerjaan-utama/${data.id_pekerjaan_utama}`
      );
      const pekerjaanData = response.data;

      setFormUtama({
        nama_pekerjaan: pekerjaanData.nama_pekerjaan,
      });

      setSubPekerjaan(
        pekerjaanData.sub_pekerjaan.map((sub) => ({
          uraian_pekerjaan: sub.uraian_pekerjaan,
          volume: Number(sub.volume) || 0,
          satuan: sub.satuan,
          harga_satuan: Number(sub.harga_satuan) || 0,
        }))
      );

      setIsEdit(true);
      setEditId(data.id_pekerjaan_utama);
      setOpenModal(true);
    } catch (error) {
      swal("Gagal", "Gagal mengambil data untuk edit", "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await swal({
      title: "Apakah Anda yakin?",
      text: "Data pekerjaan utama dan semua sub pekerjaannya akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    });

    if (result) {
      try {
        setLoadingDelete(true);
        await axios.delete(`${API_HOHOLOK}pekerjaan-utama/${id}`);
        swal("Sukses", "Data pekerjaan utama berhasil dihapus!", "success");

        // Refresh data setelah delete berhasil
        if (selectedProyek && selectedProyek.value) {
          await fetchPekerjaanProyek(selectedProyek.value);
          setRefreshKey((prev) => prev + 1);
        }
      } catch (err) {
        console.error("Error deleting pekerjaan utama:", err);
        swal("Gagal", "Gagal menghapus data pekerjaan utama", "error");
      } finally {
        setLoadingDelete(false);
      }
    }
  };

  // Fungsi untuk navigasi ke halaman kurva target realisasi proyek
  const handleKurvaTargetRealisasi = () => {
    if (selectedProyek && selectedProyek.value) {
      navigate(
        `/pages/proyek/kurva-target-realisasi-proyek/${selectedProyek.value}`
      );
    } else {
      swal("Peringatan", "Silakan pilih proyek terlebih dahulu", "warning");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          <MDBox mb={2} display="flex" alignItems="center" gap={2}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={proyekList}
                      value={selectedProyek}
                      onChange={(event, newValue) =>
                        setSelectedProyek(newValue)
                      }
                      loading={loading}
                      getOptionLabel={(option) => option.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Pilih Proyek"
                          variant="standard"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      sx={{ minWidth: 250 }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={2}
                  >
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={handleKurvaTargetRealisasi}
                      disabled={!selectedProyek}
                    >
                      Kurva Target Realisasi
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenModal}
                    >
                      Tambah Pekerjaan
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </MDBox>
          <Dialog
            open={openModal}
            onClose={handleCloseModal}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              {isEdit
                ? "Edit Pekerjaan Utama & Sub Pekerjaan"
                : "Tambah Pekerjaan Utama & Sub Pekerjaan"}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
              <DialogContent dividers>
                <Grid
                  container
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 1.5, ml: 1, mr: 1 }}
                >
                  <Grid item xs={12}>
                    <TextField
                      label="Nama Pekerjaan Utama"
                      name="nama_pekerjaan"
                      value={formUtama.nama_pekerjaan}
                      onChange={handleUtamaChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <strong>Sub Pekerjaan</strong>
                  </Grid>
                  {subPekerjaan.map((sub, idx) => (
                    <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          label="Uraian Pekerjaan"
                          name="uraian_pekerjaan"
                          value={sub.uraian_pekerjaan}
                          onChange={(e) => handleSubChange(idx, e)}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          label="Volume"
                          name="volume"
                          value={sub.volume}
                          onChange={(e) => handleSubChange(idx, e)}
                          type="number"
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Autocomplete
                          freeSolo
                          options={satuanOptions}
                          value={sub.satuan}
                          onChange={(_, newValue) =>
                            handleSubChange(idx, {
                              target: { name: "satuan", value: newValue || "" },
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Satuan"
                              name="satuan"
                              value={sub.satuan}
                              onChange={(e) => handleSubChange(idx, e)}
                              fullWidth
                              required
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          label="Harga Satuan"
                          name="harga_satuan"
                          value={sub.harga_satuan || ""}
                          onChange={(e) => handleSubChange(idx, e)}
                          type="number"
                          step="0.01"
                          min="0"
                          fullWidth
                          required
                          inputProps={{ inputMode: "numeric" }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={1}
                        display="flex"
                        alignItems="center"
                      >
                        <Button
                          onClick={() => handleRemoveSub(idx)}
                          color="error"
                          size="small"
                          disabled={subPekerjaan.length === 1}
                        >
                          Hapus
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button onClick={handleAddSub} color="info" size="small">
                      Tambah Sub Pekerjaan
                    </Button>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseModal}>Batal</Button>
                <Button type="submit" variant="contained" color="primary">
                  {isEdit ? "Update" : "Simpan"}
                </Button>
              </DialogActions>
            </form>
          </Dialog>
          {selectedProyek && (
            <MDBox mt={1} p={2} borderRadius={2} bgcolor="#f5f5f5">
              <MDTypography variant="button" fontWeight="bold">
                Proyek Terpilih:
              </MDTypography>
              <MDTypography variant="body2">
                {selectedProyek.label}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Lokasi: {selectedProyek.lokasi || "-"}
              </MDTypography>
            </MDBox>
          )}
          <Card>
            <MDBox p={3} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Detail item pekerjaan proyek
              </MDTypography>
              <MDTypography variant="button" color="text">
                Tabel pekerjaan utama dan sub pekerjaan proyek terpilih.
              </MDTypography>
            </MDBox>
            <DataTable
              table={{ columns: tableColumns, rows: tableRows }}
              entriesPerPage={{ defaultValue: 100, values: [100] }}
            />
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Detail_Proyek;
